import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  IconButton,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PhoneIcon from '@mui/icons-material/Phone';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HomeIcon from '@mui/icons-material/Home';
import FlagIcon from '@mui/icons-material/Flag';
import { Task } from '@tassomutuo-backoffice/types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import EditTaskModal from '../EditTaskModal';
import CheckIcon from '@mui/icons-material/Check';
import ScheduleIcon from '@mui/icons-material/Schedule';
import formatMoney, {
  formatNumberWithThousandsSeparator,
} from '../../utils/formatMoney';

interface TaskDetailModalProps {
  open: boolean;
  task: Task | null;
  onClose: () => void;
  onDelete: (taskId: string) => void;
  onUpdate: (
    taskId: string,
    data: {
      action?: string;
      notes?: string;
      date?: string;
      completed?: boolean;
    }
  ) => void;
  isDeleting?: boolean;
  isUpdating?: boolean;
  currentView?: 'calendar' | 'list';
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  open,
  task,
  onClose,
  onDelete,
  onUpdate,
  isDeleting = false,
  isUpdating = false,
  currentView = 'calendar',
}) => {
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!task) return null;

  const handleDelete = () => {
    onDelete(task.id);
    setShowDeleteConfirm(false);
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleEditClose = () => {
    setIsEditModalOpen(false);
  };

  const handleEditSubmit = (data: {
    action: string;
    notes?: string;
    date: string;
  }) => {
    onUpdate(task.id, data);
    setIsEditModalOpen(false);
    onClose();
  };

  const handleNavigateToLead = () => {
    const fromParam = currentView === 'list' ? 'tasks-list' : 'tasks';
    navigate(`/lead/${task.lead.id}?from=${fromParam}`);
    onClose();
  };

  const handleToggleComplete = () => {
    onUpdate(task.id, { completed: !task.completed });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">Dettagli promemoria</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Lead
                </Typography>
                <Button
                  onClick={handleNavigateToLead}
                  size="small"
                  color="primary"
                  endIcon={<OpenInNewIcon fontSize="small" />}
                  sx={{ minWidth: 'auto' }}
                >
                  Vai alla lead
                </Button>
              </Box>
              <Typography variant="body1" fontWeight="medium">
                {task.lead.properties.dealname}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {task.lead.id}
              </Typography>

              {task.lead.properties.readable_dealstage && (
                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                  }}
                >
                  <FlagIcon
                    sx={{ fontSize: '1.2rem', color: 'primary.main' }}
                  />
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}
                    >
                      Stato
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {task.lead.properties.readable_dealstage.label}
                    </Typography>
                  </Box>
                </Box>
              )}

              {task.lead.properties.phone_number && (
                <Box
                  sx={{
                    mt: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    backgroundColor: '#f0f7ff',
                    borderRadius: 1,
                    border: '1px solid #e3f2fd',
                  }}
                >
                  <PhoneIcon
                    sx={{ fontSize: '1.2rem', color: 'primary.main' }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block' }}
                    >
                      Telefono
                    </Typography>
                    <Typography
                      variant="body2"
                      component="a"
                      href={`tel:${task.lead.properties.phone_number}`}
                      fontWeight={500}
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {task.lead.properties.phone_number}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box
                sx={{
                  mt: 1.5,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1.5,
                }}
              >
                {task.lead.properties.loan_amount && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.5,
                      backgroundColor: '#fff4e6',
                      borderRadius: 1,
                      border: '1px solid #ffe4cc',
                    }}
                  >
                    <AccountBalanceIcon
                      sx={{ fontSize: '1.2rem', color: '#ff9800' }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', fontSize: '0.65rem' }}
                      >
                        Importo mutuo
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatNumberWithThousandsSeparator(
                          formatMoney(+task.lead.properties.loan_amount)
                        )}{' '}
                        €
                      </Typography>
                    </Box>
                  </Box>
                )}

                {task.lead.properties.property_value && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.5,
                      backgroundColor: '#f1f8e9',
                      borderRadius: 1,
                      border: '1px solid #dcedc8',
                    }}
                  >
                    <HomeIcon sx={{ fontSize: '1.2rem', color: '#689f38' }} />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', fontSize: '0.65rem' }}
                      >
                        Valore immobile
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {formatNumberWithThousandsSeparator(
                          formatMoney(+task.lead.properties.property_value)
                        )}{' '}
                        €
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="caption" color="text.secondary">
                Data e Ora
              </Typography>
              <Typography variant="body1">
                {format(new Date(task.date), "dd MMMM yyyy 'alle' HH:mm", {
                  locale: it,
                })}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Azione
              </Typography>
              <Typography variant="body1">{task.action}</Typography>
            </Box>

            {task.notes && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Note
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {task.notes}
                </Typography>
              </Box>
            )}

            <Divider />

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Stato:
                </Typography>

                <Chip
                  icon={
                    task.completed ? (
                      <CheckIcon sx={{ fontSize: '1rem' }} />
                    ) : (
                      <ScheduleIcon sx={{ fontSize: '1rem' }} />
                    )
                  }
                  label={task.completed ? 'Fatto' : 'Da fare'}
                  size="small"
                  sx={{
                    borderRadius: '5px',
                    backgroundColor: task.completed ? '#DDF8F2' : '#FFEECC',
                    color: task.completed ? '#059573' : '#F9A602',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: '20px',
                    alignSelf: 'flex-start',
                    '& .MuiChip-icon': {
                      color: task.completed ? '#059573' : '#F9A602',
                    },
                  }}
                />
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  task.completed ? (
                    <RadioButtonUncheckedIcon />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                onClick={handleToggleComplete}
                disabled={isUpdating || isDeleting}
                color={task.completed ? 'inherit' : 'success'}
              >
                {task.completed
                  ? 'Segna come non completato'
                  : 'Segna come completato'}
              </Button>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Creato il
              </Typography>
              <Typography variant="body2">
                {format(new Date(task.createdAt), 'dd/MM/yyyy HH:mm', {
                  locale: it,
                })}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          {!showDeleteConfirm ? (
            <>
              <Button
                startIcon={<DeleteIcon />}
                onClick={() => setShowDeleteConfirm(true)}
                color="error"
                disabled={isDeleting || isUpdating}
              >
                Elimina
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={onClose} disabled={isDeleting || isUpdating}>
                  Chiudi
                </Button>
                <Button
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  variant="contained"
                  disabled={isDeleting || isUpdating}
                >
                  Modifica
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" color="error">
                Sei sicuro di voler eliminare questo promemoria?
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleDelete}
                  color="error"
                  variant="contained"
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Eliminazione...' : 'Conferma'}
                </Button>
              </Box>
            </>
          )}
        </DialogActions>
      </Dialog>

      <EditTaskModal
        open={isEditModalOpen}
        task={task}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
        isLoading={isUpdating}
      />
    </>
  );
};

export default TaskDetailModal;
